import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import { ChevronRight } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { differenceInDays, format } from "date-fns";
import { useState } from "react";
import ImageCarousel from "@/components/ImageCarousel";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { useCurrency } from "@/hooks/useCurrency";
import { usePriceCheck } from "@/hooks/usePriceCheck";
import type {
  AvailabilityCampsite,
  Campsite,
  CampsiteData,
  CampsiteImage,
  PriceCheckResponse,
  TopAttribute,
} from "@/types/api";
import { analytics } from "@/utils/analytics";
import { getDateFnsLocale } from "@/utils/booking";
import { formatCurrency } from "@/utils/pricing";
import AlternativeDateOptions from "./AlternativeDateOptions";
import CampsiteDetailDialog from "./CampsiteDetailDialog";

// ─── Shared helpers ───────────────────────────────────────────────────────────

interface PriceDisplayProps {
  isAvailable: boolean;
  priceInfo: AvailabilityCampsite["prices"];
  nights: number;
  guests: number;
  altPrice?: PriceCheckResponse | null;
  altPriceLoading?: boolean;
  formatAltPrice?: (value: number) => string | null;
  formatAltPriceAlt?: (value: number) => string | null;
  selectedAlternativePeriod?: { arrival: Date; departure: Date } | null;
  align?: "start" | "end";
}

function PriceDisplay({
  priceInfo,
  nights,
  guests,
  altPrice,
  altPriceLoading,
  formatAltPrice,
  formatAltPriceAlt,
  selectedAlternativePeriod,
  align = "end",
}: PriceDisplayProps) {
  const { data: campgroundData } = useCampground();
  const { currencyCode } = useCurrency();
  const { i18n } = useLingui();
  const dateFnsLocale = getDateFnsLocale(i18n.locale);

  const periodLabel =
    selectedAlternativePeriod &&
    `(${format(selectedAlternativePeriod.arrival, "MMM d", { locale: dateFnsLocale })} – ${format(selectedAlternativePeriod.departure, "MMM d", { locale: dateFnsLocale })})`;

  return (
    <Stack alignItems={align}>
      {periodLabel && (
        <Typography variant="body2" color="text.secondary">
          {periodLabel}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" gutterBottom>
        <Trans>
          Price for {nights} <Plural value={nights} one="night" other="nights" />, {guests}{" "}
          <Plural value={guests} one="guest" other="guests" />
        </Trans>
      </Typography>
      {altPriceLoading ? (
        <Skeleton variant="text" width={100} />
      ) : altPrice?.total && formatAltPrice ? (
        <>
          <Typography variant="h2">{formatAltPrice(altPrice.total)}</Typography>
          {altPrice.currency_alt?.total && formatAltPriceAlt && (
            <Typography color="text.secondary">
              ({formatAltPriceAlt(altPrice.currency_alt.total)})
            </Typography>
          )}
        </>
      ) : priceInfo?.total ? (
        <>
          <Typography variant="h2">
            {formatCurrency(priceInfo.total, campgroundData.campground.currency, i18n.locale)}
          </Typography>
          {priceInfo.total_alt && (
            <Typography color="text.secondary">
              ({formatCurrency(priceInfo.total_alt, currencyCode, i18n.locale)})
            </Typography>
          )}
        </>
      ) : (
        <Typography>
          <Trans>No price available</Trans>
        </Typography>
      )}
    </Stack>
  );
}

function renderTopAttributes(attributes: TopAttribute[]) {
  return attributes.map((attribute) => (
    <Chip
      key={attribute.name}
      label={attribute.name}
      icon={
        attribute.icon_svg ? (
          <Box
            sx={{
              width: 16,
              height: 16,
              display: "flex",
              "& svg": { width: "100%", height: "100%" },
            }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG icon from trusted backend API
            dangerouslySetInnerHTML={{ __html: attribute.icon_svg }}
          />
        ) : undefined
      }
      sx={{ backgroundColor: "grey.200", color: "text.secondary", fontSize: "body1.fontSize" }}
    />
  ));
}

// ─── Component ────────────────────────────────────────────────────────────────

interface CampsiteCardProps {
  campsiteData: CampsiteData;
  onSelect: (campsite: Campsite) => void;
  onShowAlternativeDates: (accoId: number) => void;
  onSelectAlternativeDates?: (dateFrom: Date, dateUntil: Date) => void;
  nights?: number;
  guests?: number;
  dateFrom?: Date;
}

export default function CampsiteCard({
  campsiteData,
  onSelect,
  onShowAlternativeDates,
  onSelectAlternativeDates,
  nights = 3,
  guests = 2,
  dateFrom,
}: CampsiteCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { state } = useBookingContext();
  const {
    price: altPrice,
    loading: altPriceLoading,
    checkPrice,
    formatPrice,
    formatPriceAlt,
  } = usePriceCheck();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAlternative, setSelectedAlternative] = useState<{
    arrival: Date;
    departure: Date;
  } | null>(null);

  const { availabilityCampsite, basicCampsite } = campsiteData;
  const isAvailable = availabilityCampsite.amount > 0;
  const priceInfo = availabilityCampsite.prices;
  const images: CampsiteImage[] = basicCampsite.images || [];
  const topAttributes = [
    basicCampsite.top1_attribute,
    basicCampsite.top2_attribute,
    basicCampsite.top3_attribute,
  ].filter((a) => a !== null);
  const plainTextDescription = basicCampsite.description.replace(/<[^>]*>/g, "");
  const shouldTruncate = plainTextDescription.length > 200;
  const showAlternativeDates = !isAvailable && availabilityCampsite.can_show_alternative_dates;

  const handleSelect = () => {
    if (selectedAlternative && onSelectAlternativeDates) {
      analytics.capture("alternative_dates_selected", {
        accommodation_name: basicCampsite.name,
        accommodation_type: basicCampsite.campsite_type,
        date_from: format(selectedAlternative.arrival, "yyyy-MM-dd"),
        date_until: format(selectedAlternative.departure, "yyyy-MM-dd"),
        nights: differenceInDays(selectedAlternative.departure, selectedAlternative.arrival),
        source: "card",
      });
      onSelectAlternativeDates(selectedAlternative.arrival, selectedAlternative.departure);
    }
    onSelect(basicCampsite);
  };

  const handleShowAlternativeDates = () => {
    analytics.capture("alternative_dates_viewed", {
      accommodation_name: basicCampsite.name,
      accommodation_type: basicCampsite.campsite_type,
    });
    onShowAlternativeDates(availabilityCampsite.id);
  };

  const handleAlternativeOptionChange = (arrival: Date, departure: Date) => {
    setSelectedAlternative({ arrival, departure });
    checkPrice({
      dateFrom: arrival,
      dateUntil: departure,
      campsiteId: availabilityCampsite.id,
      persons: state.selectedPersons,
    }).catch(() => {});
  };

  // Shared price display props
  const priceDisplayProps: PriceDisplayProps = {
    isAvailable,
    priceInfo,
    nights,
    guests,
    altPrice,
    altPriceLoading,
    formatAltPrice: formatPrice,
    formatAltPriceAlt: formatPriceAlt,
    selectedAlternativePeriod: selectedAlternative,
  };

  // Shared alternative date options (only rendered when applicable)
  const alternativeDateOptions = !isAvailable && dateFrom && (
    <AlternativeDateOptions
      accoId={availabilityCampsite.id}
      canShowAlternativeDates={availabilityCampsite.can_show_alternative_dates}
      nights={nights}
      searchedDateFrom={dateFrom}
      reasonTxt={availabilityCampsite.reason_txt}
      onOptionChange={handleAlternativeOptionChange}
      split={!isMobile}
    />
  );

  // Shared "not available" chip
  const unavailableChip = !isAvailable && (
    <Chip
      label={t`not available`}
      color="error"
      sx={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}
    />
  );

  // ─── Desktop layout ─────────────────────────────────────────────────────────
  const desktopCard = (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        cursor: "pointer",
      }}
      onClick={() => setDetailDialogOpen(true)}
    >
      {/* Top section: image + content columns */}
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <CardMedia
          component="div"
          sx={{
            width: 220,
            aspectRatio: "1 / 1",
            flexShrink: 0,
            bgcolor: "grey.300",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            m: 2,
            borderRadius: 2,
          }}
        >
          {unavailableChip}
          <ImageCarousel images={images} variant="1_1" />
        </CardMedia>

        {/* Left column (description) + right column (alt dates) */}
        <CardContent
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Name, description, attribute chips */}
          <Stack direction="column" flex={1}>
            <Typography variant="h3">{basicCampsite.name}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from trusted backend API
              dangerouslySetInnerHTML={{ __html: basicCampsite.description }}
              sx={{
                ...(shouldTruncate && {
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }),
              }}
              gutterBottom
            />
            {shouldTruncate && (
              <Link
                variant="body2"
                gutterBottom
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailDialogOpen(true);
                }}
              >
                <Trans>Show more</Trans>
              </Link>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {renderTopAttributes(topAttributes)}
            </Stack>
          </Stack>

          {/* Alt date options (constraints + alternatives) */}
          {alternativeDateOptions && (
            <Stack direction="column" flex={1}>
              {alternativeDateOptions}
            </Stack>
          )}
        </CardContent>
      </Box>

      {/* Bottom row: price (left) + Select button (right) */}
      {(isAvailable || selectedAlternative) && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <PriceDisplay {...priceDisplayProps} align="start" />
            <Button
              variant="contained"
              size="large"
              disabled={(selectedAlternative && !altPrice) || (isAvailable && !priceInfo?.total)}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect();
              }}
            >
              <Trans>Select</Trans>
            </Button>
          </Stack>
        </>
      )}

      {/* Footer: view alternative availability */}
      {showAlternativeDates && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
            sx={{ p: 2, cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleShowAlternativeDates();
            }}
          >
            <Typography variant="h4" color="primary" fontWeight={600}>
              <Trans>View alternative availability</Trans>
            </Typography>
            <ChevronRight color="primary" fontSize="small" />
          </Stack>
        </>
      )}
    </Card>
  );

  // ─── Mobile layout ──────────────────────────────────────────────────────────
  const mobileCard = (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        border: 1,
        borderColor: "divider",
        cursor: "pointer",
      }}
      onClick={() => setDetailDialogOpen(true)}
    >
      <CardMedia
        component="div"
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          flexShrink: 0,
          bgcolor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {unavailableChip}
        <ImageCarousel images={images} variant="1_1" />
      </CardMedia>

      {/* Name, description, alt date options, price, Select button */}
      <CardContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
        <Stack direction="column" p={2}>
          <Typography variant="h3">{basicCampsite.name}</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from trusted backend API
            dangerouslySetInnerHTML={{ __html: basicCampsite.description }}
            sx={{
              ...(shouldTruncate && {
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }),
            }}
            gutterBottom
          />
          {shouldTruncate && (
            <Link
              variant="body2"
              gutterBottom
              onClick={(e) => {
                e.stopPropagation();
                setDetailDialogOpen(true);
              }}
            >
              <Trans>Show more</Trans>
            </Link>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {renderTopAttributes(topAttributes)}
          </Stack>
        </Stack>

        {alternativeDateOptions}

        {(isAvailable || selectedAlternative) && (
          <Stack>
            <Box px={2} pt={selectedAlternative ? 2 : 0}>
              <PriceDisplay {...priceDisplayProps} />
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect();
              }}
              disabled={(selectedAlternative && !altPrice) || (isAvailable && !priceInfo?.total)}
              sx={{ m: 2, mb: showAlternativeDates ? 2 : 0 }}
            >
              <Trans>Select</Trans>
            </Button>
            {!isAvailable && <Divider />}
          </Stack>
        )}
      </CardContent>

      {/* "View alternative availability" footer — pinned to card bottom with divider */}
      {showAlternativeDates && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleShowAlternativeDates();
          }}
        >
          <Typography variant="h4" color="primary" fontWeight={600}>
            <Trans>View alternative availability</Trans>
          </Typography>
          <ChevronRight color="primary" fontSize="small" />
        </Stack>
      )}
    </Card>
  );

  return (
    <>
      {isMobile ? mobileCard : desktopCard}
      <CampsiteDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        campsite={basicCampsite}
        isAvailable={isAvailable}
        images={images}
        onSelect={onSelect}
        priceInfo={priceInfo}
        nights={nights}
        guests={guests}
        showAlternativeDates={showAlternativeDates}
        onShowAlternativeDates={handleShowAlternativeDates}
      />
    </>
  );
}
